<script setup>
import { computed, ref } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { $api } from '@/utils/api'

definePage({
  meta: {
    layout: 'default',
    requiresAuth: true,
    roles: ['surgeon_secretary'],
  },
})

const authStore = useAuthStore()
const currentUser = computed(() => authStore.currentUser)

const tier = ref(currentUser.value?.subscription_tier ?? 'standard')
const loading = ref(false)
const error = ref('')
const success = ref('')

const tierOptions = [
  { label: 'Standard (free)', value: 'standard' },
  { label: 'Premium', value: 'premium' },
]

const updateTier = async () => {
  loading.value = true
  error.value = ''
  success.value = ''

  try {
    await $api('/api/profile/subscription-tier', {
      method: 'PUT',
      body: {
        subscription_tier: tier.value,
      },
    })

    await authStore.fetchAuthenticatedUser()
    success.value = 'Subscription tier updated.'
  } catch (err) {
    error.value = err?.response?._data?.message
      ?? err?.data?.message
      ?? err?.message
      ?? 'Unable to update the subscription tier.'
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <VContainer class="py-6">
    <VRow justify="center">
      <VCol
        cols="12"
        md="8"
        lg="6"
      >
        <VCard>
          <VCardTitle class="text-h5">
            Profile
          </VCardTitle>
          <VDivider />
          <VCardText>
            <div class="mb-4">
              <div class="text-body-2 text-medium-emphasis">
                Name
              </div>
              <div class="text-body-1">
                {{ currentUser?.name ?? '—' }}
              </div>
            </div>
            <div class="mb-4">
              <div class="text-body-2 text-medium-emphasis">
                Email
              </div>
              <div class="text-body-1">
                {{ currentUser?.email ?? '—' }}
              </div>
            </div>

            <VSelect
              v-model="tier"
              :items="tierOptions"
              item-title="label"
              item-value="value"
              label="Subscription tier"
              variant="outlined"
              class="mb-4"
            />

            <VAlert
              v-if="error"
              type="error"
              variant="tonal"
              class="mb-4"
            >
              {{ error }}
            </VAlert>
            <VAlert
              v-if="success"
              type="success"
              variant="tonal"
              class="mb-4"
            >
              {{ success }}
            </VAlert>
          </VCardText>
          <VCardActions>
            <VSpacer />
            <VBtn
              color="primary"
              :loading="loading"
              @click="updateTier"
            >
              Save changes
            </VBtn>
          </VCardActions>
        </VCard>
      </VCol>
    </VRow>
  </VContainer>
</template>
